// React hook for screens to register handlers for their bindings.
//   useTallyShortcut('edit.accept', () => save());
// On mount: registers the handler. On unmount: unregisters.
//
// The global key listener (in useGlobalShortcuts) is what actually fires the handler.
//
// IMPLEMENTATION NOTE: handlers are wrapped with a ref so the latest closure is
// always invoked even if deps change. This avoids the stale-closure bug where
// `useTallyShortcut('window.escape', () => { if (open) setOpen(false); })` captured
// `open === false` at mount and never saw the updated state.

import { useEffect, useRef } from 'react';
import { useShortcutRegistry, type Handler } from '@/shortcuts/registry';

export function useTallyShortcut(bindingId: string, handler: Handler, enabled = true) {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;
    const stableWrapper: Handler = () => handlerRef.current();
    const unregister = useShortcutRegistry.getState().registerHandler(bindingId, stableWrapper);
    return unregister;
  }, [bindingId, enabled]);
}

/**
 * Hook to set the active scope while a component is mounted. Resets to '*' on unmount.
 */
export function useShortcutScope(scope: string) {
  useEffect(() => {
    const prev = useShortcutRegistry.getState().scope;
    useShortcutRegistry.getState().setScope(scope);
    return () => useShortcutRegistry.getState().setScope(prev);
  }, [scope]);
}
